import * as React from "react";

export interface VideoProps {
  videoUrl?: string; // Legacy prop for backward compatibility
  videoUrls?: {
    default: string;
    mobile: string;
    tablet: string;
    desktop: string;
    ultrawide: string;
  };
  enableResponsiveVideos?: boolean;
  ref?: React.RefObject<HTMLVideoElement> | null;
  controls?: boolean;
  type?: string;
  autoplay?: boolean;
  muted?: boolean;
  playsInline?: boolean;
  loop?: boolean;
  classes?: string;
  aspectRatio?: string;
  objectFit?: 'contain' | 'cover' | 'fill';
  sizingMode?: 'aspect_ratio' | 'custom_height' | 'responsive' | 'screen_height';
  customHeight?: number;
  borderRadius?: number;
  priority?: boolean;
  poster?: string;
}

export const Video = ({
  priority = false,
  poster,
  videoUrl, // Legacy prop
  videoUrls,
  enableResponsiveVideos = false,
  ref = null,
  controls = false,
  type = 'video/mp4',
  autoplay = false,
  muted = true,
  playsInline = true,
  loop = true,
  classes = "",
  aspectRatio = "16:9",
  objectFit = "cover",
  sizingMode = "aspect_ratio",
  customHeight,
  borderRadius = 0
}: VideoProps) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [isIntersecting, setIsIntersecting] = React.useState(priority);
  const [currentBreakpoint, setCurrentBreakpoint] = React.useState<string>('desktop');

  // Function to get the appropriate video URL based on breakpoint and fallback logic
  const getVideoUrl = React.useCallback(() => {
    // If videoUrls is provided, use the responsive logic
    if (videoUrls) {
      // If responsive videos are enabled, use breakpoint-specific videos with fallback
      if (enableResponsiveVideos) {
        let url = '';
        
        switch (currentBreakpoint) {
          case 'mobile':
            url = videoUrls.mobile || videoUrls.default;
            break;
          case 'tablet':
            url = videoUrls.tablet || videoUrls.default;
            break;
          case 'desktop':
            url = videoUrls.desktop || videoUrls.default;
            break;
          case 'ultrawide':
            url = videoUrls.ultrawide || videoUrls.default;
            break;
          default:
            url = videoUrls.default;
        }
        
        return url;
      } else {
        // If responsive videos are not enabled, always use the default video
        return videoUrls.default;
      }
    }
    
    // Fallback to legacy videoUrl prop if videoUrls is not provided
    return videoUrl || '';
  }, [enableResponsiveVideos, videoUrls, currentBreakpoint, videoUrl]);
  
  // Function to detect current breakpoint
  const detectBreakpoint = React.useCallback(() => {
    const width = window.innerWidth;
    
    if (width < 768) {
      return 'mobile';
    } else if (width < 1024) {
      return 'tablet';
    } else if (width < 1920) {
      return 'desktop';
    } else {
      return 'ultrawide';
    }
  }, []);
    // Set up resize listener to detect breakpoint changes
  React.useEffect(() => {
    const handleResize = () => {
      const newBreakpoint = detectBreakpoint();
      setCurrentBreakpoint(newBreakpoint);
    };
    
    // Set initial breakpoint
    handleResize();
    
    // Only add resize listener if responsive videos are enabled
    if (enableResponsiveVideos) {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [enableResponsiveVideos, detectBreakpoint]);
  
  // Preload priority video
  React.useEffect(() => {
    if (priority && poster) {
      // Preload poster image
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = poster;
      document.head.appendChild(link);
      return () => document.head.removeChild(link);
    }
    if (priority && !poster) {
      // Preload video source
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'video';
      link.href = getVideoUrl();
      link.type = type;
      document.head.appendChild(link);
      return () => document.head.removeChild(link);
    }
  }, [priority, poster, type]);

  React.useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsIntersecting(true);
          observer.disconnect();
        }
      });
    }, { rootMargin: '200px' });
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const currentVideoUrl = getVideoUrl();
  if (!currentVideoUrl) return null;

  // Generate styles based on props
  const containerStyle: React.CSSProperties = {};
  const videoStyle: React.CSSProperties = {
    width: '100%',
    objectFit
  };

  // Apply border radius if specified
  if (borderRadius > 0) {
    containerStyle.borderRadius = `${borderRadius}px`;
    containerStyle.overflow = 'hidden'; // Ensure video content respects border radius
    videoStyle.borderRadius = `${borderRadius}px`;
  }

  // Apply sizing based on mode
  if (sizingMode === 'aspect_ratio') {
    // Calculate aspect ratio for padding-bottom
    let paddingBottom = '56.25%'; // Default 16:9
    
    switch (aspectRatio) {
      case '1:1':
        paddingBottom = '100%';
        break;
      case '4:3':
        paddingBottom = '75%';
        break;
      case '9:16':
        paddingBottom = '177.78%';
        break;
      case '21:9':
        paddingBottom = '42.86%';
        break;
      default: 
        paddingBottom = '56.25%'; // 16:9
    }
    
    containerStyle.position = 'relative';
    containerStyle.paddingBottom = paddingBottom;
    containerStyle.height = 0;
    containerStyle.overflow = borderRadius > 0 ? 'hidden' : 'hidden'; // Always hidden for aspect ratio
    
    videoStyle.position = 'absolute';
    videoStyle.top = 0;
    videoStyle.left = 0;
    videoStyle.width = '100%';
    videoStyle.height = '100%';
  } else if (sizingMode === 'custom_height' && customHeight) {
    containerStyle.height = `${customHeight}px`;
    videoStyle.height = '100%';
  } else if (sizingMode === 'screen_height') {
    videoStyle.height = '100%';
  }
  return (
    <div ref={containerRef} className={`video-container ${classes}`} style={containerStyle}>
      {isIntersecting && (
        <video
          poster={poster}
          preload={autoplay ? 'auto' : 'metadata'}
          ref={ref}
          style={videoStyle}
          autoPlay={autoplay}
          muted={muted || autoplay}
          loop={loop}
          controls={controls}
          playsInline={playsInline}
          key={currentVideoUrl}
        >
          <source src={currentVideoUrl} type={type}/>
          Your browser does not support the video tag.
        </video>
      )}
    </div>
  );
};

export default Video;
